import { computed, ref } from "vue";
import { IImportVideoApiService, IVideoImportService } from "../interfaces";
import {
  AbortVideoRequestModel,
  Events,
  ImportVideoModel,
  InitImportFilesRequestModel,
  ResponseModel,
} from "../models";
import {
  ControlParams,
  EventsType,
  ImportBatchParams,
  ImportItem,
  ImportParams,
  ImportProgressEventData,
  InitImportVideoItem,
} from "../types";
import { FileUploadStatusEnum } from "../enums";

export class VideoImportService implements IVideoImportService {
  constructor(private api: IImportVideoApiService) {
    this.subscribeEvents();
  }
  /**
   * Текущие активные импорты видео
   * @returns {ComputedRef<Array<ImportVideoModel>>} Реактивный список видео в процессе импорта
   */
  private activeUploads = ref(new Array<ImportVideoModel>());
  /**
   * Текущие активные импорты видео
   * @returns {ComputedRef<Array<ImportVideoModel>>} Реактивный список видео в процессе импорта
   */
  readonly ActiveUploads = computed(() => this.activeUploads.value);
  /**Событие завершения импорта видеофайла */
  readonly CompleteEvent: EventsType<ImportVideoModel> =
    new Events<ImportVideoModel>();
  /**Событие начала обработки файла */
  readonly StartProcessingEvent: EventsType<ImportVideoModel> =
    new Events<ImportVideoModel>();

  /**Подписаться на события */
  private subscribeEvents = () => {
    this.api.ProgressEvent.on(this.onUpdateProgress);
    this.api.CompleteEvent.on(this.onComplete);
  };
  /**
   * Обработчик события обновления прогресса импорта файла
   * @param event Событие
   * @returns
   */
  private onUpdateProgress = (event: ImportProgressEventData) => {
    const file = this.getFile(event.FileId);
    if (!file) return;
    file.Progress = event.ProgressDownload;
    if(event.ProgressDownload === 100) {
      file.Status = FileUploadStatusEnum.Processing;
      this.StartProcessingEvent.trigger(file);
    }
  };

  /**
   * Обработчик события успешного импорта видеофайла
   * @param fileId ID файла
   */
  private onComplete = (fileId: number) => {
    const file = this.getFile(fileId);
    if (file) {
      file.Status = FileUploadStatusEnum.Uploaded;
      this.CompleteEvent.trigger(file);
    }
  };

  /**Возвращает файл из списка импортируемых, если он там есть */
  private getFile = (fileId: number): ImportVideoModel | undefined => {
    return this.activeUploads.value.find((file) => file.FileId === fileId);
  };

  /**Проверяет, предназначены ли параметры для batch импорта */
  private isImportBatchParams(
    params: ImportParams | ImportBatchParams
  ): params is ImportBatchParams {
    return Array.isArray((params as ImportBatchParams).Items);
  }

  /**Создает запрос на импорт файлов */
  private createRequestImportFiles = (
    params: ImportParams | ImportBatchParams
  ): InitImportFilesRequestModel => {
    const items: Array<ImportItem> = this.isImportBatchParams(params)
      ? params.Items
      : [params.Item];

    return new InitImportFilesRequestModel({
      FolderId: params.Location.FolderId,
      TaskId: params.Location.TaskId,
      QueueTaskId: params.Location.QueueTaskId,
      ChatId: params.Location.ChatId,
      Files: items.map((item) => ({
        FileName: item.FileName,
        FileUrl: item.FileUrl,
      })),
    });
  };

  /**
   * Создает файлы на основании параметров импорта и ответа инициализации импорта файлов
   * @param params Параметры импорта
   * @param initFiles Ответ инициализации импорта файлов
   * @returns
   */
  private createFiles = (
    params: ImportParams | ImportBatchParams,
    initFiles: Array<InitImportVideoItem>
  ) => {
    const paramsItems: Array<ImportItem> = this.isImportBatchParams(params)
      ? params.Items
      : [params.Item];

    return initFiles.flatMap((file) => {
      const paramItem = paramsItems.find((item) => item.FileUrl === file.SourceUrL);
      if (!paramItem) return [];
      return [
        new ImportVideoModel({
          SourceUrl: file.SourceUrL,
          VideoId: file.VideoId,
          FileId: file.FileId,
          Location: params.Location,
          Status: FileUploadStatusEnum.Uploading,
          Progress: 0,
          Url: file.Url,
          Name: paramItem.FileName,
          Size: paramItem.FileSize,
          DateCreate: new Date(),
        }),
      ];
    });
  };
  /**
   * Импортирует одно видео по URL
   * @param {ImportParams} params - Параметры импорта
   * @returns {Promise<ResponseModel<ImportVideoModel>>} Модель импортированного видео
   */
  import(params: ImportParams): Promise<ResponseModel<ImportVideoModel>> {
    return new Promise<ResponseModel<ImportVideoModel>>(async (resolve) => {
      const request = this.createRequestImportFiles(params);

      const response = await this.api.initImport(request);

      if (response.IsSuccess && response.Value.length) {
        const files = this.createFiles(params, response.Value);

        this.activeUploads.value.push(...files);
        return resolve(
          new ResponseModel<ImportVideoModel>({
            ...response,
            Value: files?.[0],
          })
        );
      }

      resolve(
        new ResponseModel<ImportVideoModel>({
          ...response,
          Value: undefined,
        })
      );
    });
  }

  /**
   * Пакетный импорт видео по URL
   * @param {ImportBatchParams} params - Параметры импорта
   * @returns {Promise<ResponseModel<Array<ImportVideoModel>>>} Список импортированных видео
   */
  importBatch(
    params: ImportBatchParams
  ): Promise<ResponseModel<Array<ImportVideoModel>>> {
    return new Promise<ResponseModel<Array<ImportVideoModel>>>(
      async (resolve) => {
        const request = this.createRequestImportFiles(params);

        const response = await this.api.initImport(request);

        if (response.IsSuccess && response.Value.length) {
          const files = this.createFiles(params, response.Value);

          this.activeUploads.value.push(...files);
          return resolve(
            new ResponseModel<Array<ImportVideoModel>>({
              ...response,
              Value: files,
            })
          );
        }

        resolve(
          new ResponseModel<Array<ImportVideoModel>>({
            ...response,
            Value: undefined,
          })
        );
      }
    );
  }

  /**
   * Отменяет импорт видео
   * @param {ControlParams} params - Параметры управления
   */
  cancel(params: ControlParams): Promise<ResponseModel> {
    return new Promise<ResponseModel>(async (resolve) => {
      let result = new ResponseModel();

      const file = this.getFile(params.FileId);

      if (!file) {
        result.IsSuccess = false;
        return resolve(result);
      }
      const request = new AbortVideoRequestModel({
        VideoId: file.VideoId,
        FileId: file.FileId,
      });

      result = await this.api.abortImport(request);

      if(result.IsSuccess) {
        this.activeUploads.value = this.activeUploads.value.filter(
          (file) => file.FileId !== params.FileId
        );
  
      }

      return resolve(result);
    });
  }
  /**Проверяет, есть ли файл в списке импортируемых */
  isFileInActiveUploads(fileId: number): boolean {
    return this.activeUploads.value.some((file) => file.FileId === fileId);
  }
}
